// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// The simulation. One `step()` is one tick of 0.1 tower-seconds, and it takes no `dt` — the clock is
// a `setInterval` in main.ts, and rAF is only ever a render signal.
//
// ── WHAT COSTS WHAT, PER TICK ───────────────────────────────────────────────────────────────────
// Only a moving car does per-tick work. A walk, a wait and a ride are all closed-form: a walking
// person has a `wake` tick and is not touched until then, a waiting person is touched only when a car
// opens its doors, and a rider is carried by the car. So the hot loop is twelve cars and a short list
// of people who happen to be mid-walk — not the two hundred people in the building. That is the
// difference between a balance sim that runs in seconds and one that cannot exist.
//
// ── AND WHY EVERY NUMBER THE ECONOMY USES COMES FROM A PERSON ───────────────────────────────────
// Rent, footfall and satisfaction have exactly one source: events emitted by individual people
// completing individual journeys. Nothing is estimated from a per-storey demand figure, and there is
// no path by which a unit earns money without somebody having walked into it. That is a deliberate
// architectural constraint against the most likely way this game ships hollow — performance pressure
// turning the little walking dots into a screensaver over a spreadsheet — and `tests/mechanism.test.ts`
// re-derives the entire daily ledger from raw agent events to prove the dots are the game.

import type { Dir, EventSink } from './events';
import {
  boardingCol,
  canExtendRiser,
  canPlaceRiser,
  canPlaceStair,
  canPlaceUnit,
  createTower,
  extendCost,
  extendRiser,
  placeRiser,
  placeStair,
  placeUnit,
  removeUnit,
  riserCost,
  riserUpkeep,
  serves,
  servedStoreys,
  stairBetween,
  type Riser,
  type RiserPolicy,
  type Tower,
  type Unit,
  type Verdict,
} from './tower';
import { canStopAt, carStorey, createCar, dwellTicks, runTicks, specOf, stepCar, travelBounds, type Car } from './lift';
import { hashRange, hashUnit, stream, type Rng } from './rand';
import { MODES, type Mode, type ModeId } from './modes';
import {
  BASE_CAR_SLOTS,
  BUST_DAYS,
  CONCOURSE,
  COLS,
  DAY_TICKS,
  ERRAND_REACH,
  ERRAND_SHARE,
  ETD_FULL_PENALTY,
  ETD_STOP_PENALTY,
  ETD_ZONE_PENALTY,
  EW_ALPHA,
  EW_FORGET,
  EW_INIT_S,
  EW_MAX_S,
  EW_MIN_S,
  GOOD_WAIT_S,
  MAX_AGENTS,
  MAX_CARS,
  PARK_AFTER_TICKS,
  PHASE_DOWN,
  PHASE_MID,
  PHASE_NIGHT,
  RATING_OCC_WEIGHT,
  RATING_POP,
  RATING_SAT_WEIGHT,
  RATING_SIZE_WEIGHT,
  RELET_AFTER_DAYS,
  RELET_MAX_WAIT_S,
  RENT_SAT_CEIL,
  RENT_SAT_FLOOR,
  SAT_NOTICE,
  SAT_PER_GIVEUP,
  SAT_PER_TRIP,
  SAT_START,
  SELL_REFUND,
  SHOP_PER_FOOTFALL,
  STAIR_COST,
  STAIR_TICKS_PER_STOREY,
  STAIR_UPKEEP,
  STOREYS,
  STOREY_PREMIUM,
  SUMMON_LOCKOUT_TICKS,
  SUMMON_WHIFF_WINDOW_TICKS,
  TICK_S,
  UNITS,
  WALK_TICKS_PER_COL,
  type UnitKind,
} from './tuning';

/**
 * The most cars one riser will take. ONE — and this is the most important number in the game.
 *
 * Two cars in one shaft were in this design for most of a day, with the no-overtake rule as a
 * deliberate mechanic: they would bunch, and a player would have to notice. What the balance sim
 * measured instead was that buying the second car made the tower WORSE, every time. Eight seeded
 * runs: 7,519 person-seconds of waiting with one car, 11,196 with two. Adding zoning so the pair
 * split the building between them recovered a third of it and no more — still 9,939, still worse.
 *
 * A purchase that makes your building slower, discoverable only by losing a run, is not a mechanic.
 * It is a trap, and no outcome metric would ever have reported it: it simply looks like the game is
 * hard. So capacity is not something you add to a shaft. It is a SHAFT — and a shaft costs two
 * columns on every storey it passes, which is the trade this whole game is built on. One purchase,
 * one rule, and the thing you give up is legible on screen the moment you make it.
 */
export const MAX_CARS_PER_RISER = 1;

export type AgentState = 'walk' | 'wait' | 'ride' | 'stairs';

interface Agent {
  id: number;
  live: boolean;
  /** The tenancy whose satisfaction this journey moves. */
  unit: number;
  storey: number;
  /** Where this leg ends, and where the whole journey ends. */
  legTo: number;
  finalTo: number;
  state: AgentState;
  riser: number;
  dir: Dir;
  car: number;
  col: number;
  fromCol: number;
  targetCol: number;
  walkFrom: number;
  wake: number;
  waitStart: number;
  waitTicks: number;
  rideTicks: number;
  walkTicks: number;
  rideStart: number;
}

interface PendingTrip {
  tick: number;
  unit: number;
  from: number;
  to: number;
}

export interface RunStats {
  trips: number;
  giveUps: number;
  leaves: number;
  waitTicks: number;
  peakPop: number;
  bestRating: number;
  worstStorey: number;
  worstStoreyWaitS: number;
  /** Cumulative person-seconds spent standing at a landing. Turnover's whole score. */
  personSecondsLost: number;
  built: number;
  summons: number;
  policyChanges: number;
}

export interface DayRecord {
  day: number;
  cash: number;
  rating: number;
  pop: number;
  income: number;
  upkeep: number;
  meanWaitS: number;
}

const qi = (riser: number, storey: number, dir: Dir): number => (riser * STOREYS + storey) * 2 + (dir === 1 ? 0 : 1);

export class Sim {
  readonly mode: Mode;
  readonly seed: number;
  readonly tower: Tower;
  cash: number;
  day = 1;
  /** Ticks since the START OF THE DAY. Drives the phase bar and the spawn schedule, and only those. */
  tick = 0;
  /**
   * Ticks since the start of the RUN, and the clock every timer in this file is measured against.
   *
   * `tick` rewinds to zero at every night boundary, and for most of this build every timer in the
   * simulation was stored against it: a walking person's wake time, a queueing person's wait start, a
   * car's door timer, a car's idle clock. So anything caught mid-motion at midnight had its timer
   * stranded up to a whole shift in the future — a car that closed its doors at tick 3,500 sat in the
   * 'doors' state until 3,500 came round again the next day, and a person who joined a queue at 3,550
   * had a NEGATIVE wait from then on, so the patience sweep could never reach them and they never
   * gave up either. The tower quietly half-stopped every single night.
   *
   * Nothing about it looked like a bug: the cars still moved, still answered calls, still stopped
   * where they had promised. Every mechanism invariant about CORRECTNESS stayed clean. It took a
   * liveness rule — "no shift passes in which people called for a lift and no door ever opened" — to
   * make it visible at all.
   */
  private now = 0;
  over = false;
  won = false;
  /** Why the run ended, in the player's words. */
  ending = '';

  cars: Car[] = [];
  private agents: Agent[] = [];
  private freeAgents: number[] = [];
  private walking: number[] = [];
  private queues: number[][] = [];
  private hallCar: Int16Array;
  private ew: Float32Array;
  private lockoutUntil: Int32Array;
  private storeyWaitSum: Float64Array;
  private storeyWaitCount: Int32Array;
  private storeyFootfall: Int32Array;
  private pending: PendingTrip[] = [];
  private pendingIdx = 0;
  private deferred: PendingTrip[] = [];
  private nextCarId = 0;
  private bustDays = 0;
  private startPop = 0;

  stats: RunStats = {
    trips: 0,
    giveUps: 0,
    leaves: 0,
    waitTicks: 0,
    peakPop: 0,
    bestRating: 0,
    worstStorey: 0,
    worstStoreyWaitS: 0,
    personSecondsLost: 0,
    built: 0,
    summons: 0,
    policyChanges: 0,
  };
  history: DayRecord[] = [];
  rating = 0;

  private readonly emit: EventSink;
  private readonly layoutRng: Rng;
  private readonly passBuf: number[] = [];

  constructor(modeId: ModeId, seed: number, emit: EventSink = () => {}) {
    this.mode = MODES[modeId];
    this.seed = seed;
    this.emit = emit;
    this.cash = this.mode.cash;
    this.tower = createTower();
    this.layoutRng = stream(seed, `layout:${modeId}`);

    this.hallCar = new Int16Array(6 * STOREYS * 2).fill(-1);
    this.ew = new Float32Array(6 * STOREYS * 2).fill(EW_INIT_S);
    this.lockoutUntil = new Int32Array(6);
    this.storeyWaitSum = new Float64Array(STOREYS);
    this.storeyWaitCount = new Int32Array(STOREYS);
    this.storeyFootfall = new Int32Array(STOREYS);
    for (let i = 0; i < 6 * STOREYS * 2; i++) this.queues.push([]);

    for (let i = 0; i < MAX_AGENTS; i++) {
      this.agents.push(blankAgent(i));
      this.freeAgents.push(i);
    }

    this.buildOpening();
    this.scheduleDay();
  }

  // ── the opening: a tower already in trouble ───────────────────────────────────────────────────
  //
  // There is no empty lot in this game, in any mode. A management sim whose first minute is a blank
  // grid and a build menu is the shape that gets admired and not played; the fix is to hand the
  // player a building that is already going wrong and let them read it.

  private buildOpening(): void {
    const o = this.mode.opening;
    for (const [, hi, col, policy, expressFrom] of o.risers) {
      const r = placeRiser(this.tower, hi, col);
      if (policy) {
        r.policy = policy;
        if (expressFrom !== undefined) r.expressFrom = expressFrom;
      }
    }
    for (const [ri, express] of o.cars) this.installCar(this.tower.risers[ri], express, true);

    const served = servedStoreys(this.tower);
    for (let s = 1; s < o.storeys; s++) {
      let col = 0;
      while (col < COLS) {
        if (this.tower.kind[s * COLS + col] !== 0) {
          col++;
          continue;
        }
        const kind = this.pickOpeningKind(s);
        const spec = UNITS[kind];
        // The void is two columns WIDE, so a unit must not merely start outside it — its whole span
        // has to clear it. Testing the starting column alone let a three-wide office begin at column
        // eight and swallow half the void, which quietly removed the only place a second riser could
        // ever go and made the mode unwinnable on every seed.
        if (o.voidCol >= 0 && col < o.voidCol + 2 && col + spec.w > o.voidCol) {
          col = o.voidCol + 2;
          continue;
        }
        if (!canPlaceUnit(this.tower, kind, s, col, STOREYS - 1).ok) {
          col++;
          continue;
        }
        const u = placeUnit(this.tower, kind, s, col);
        // A tenancy is never handed a storey the lifts do not reach. The opening is a tower in
        // trouble, not a tower with people who physically cannot leave their own floor.
        if (served[s] && this.layoutRng() < o.letFraction) this.letUnit(u, o.startSat, false);
        else u.vacantDays = RELET_AFTER_DAYS;
        col += spec.w;
      }
    }
    this.startPop = this.population();
    this.rating = this.computeRating();
  }

  private pickOpeningKind(storey: number): UnitKind {
    const r = this.layoutRng();
    // Shops want the bottom of the building, flats want the top, offices are the middle of both.
    const high = storey / Math.max(1, this.mode.opening.storeys);
    if (r < 0.14 - 0.1 * high) return 'shop';
    if (r < 0.2) return 'service';
    if (r < 0.55 + 0.3 * high) return 'flat';
    return 'office';
  }

  // ── player actions ────────────────────────────────────────────────────────────────────────────

  canBuildUnit(kind: UnitKind, storey: number, col: number): Verdict {
    if (!this.mode.build) return { ok: false, why: 'Not in this mode' };
    if (this.cash < UNITS[kind].cost) return { ok: false, why: 'Not enough cash' };
    return canPlaceUnit(this.tower, kind, storey, col, this.mode.maxStorey);
  }

  buildUnit(kind: UnitKind, storey: number, col: number): Unit | null {
    if (!this.canBuildUnit(kind, storey, col).ok) return null;
    const u = placeUnit(this.tower, kind, storey, col);
    // A fresh build is ready to take a tenant at the next night boundary. Only a unit somebody WALKED
    // OUT of gets the cooling-off period, because that is what makes a cascade a cascade.
    u.vacantDays = RELET_AFTER_DAYS;
    this.cash -= UNITS[kind].cost;
    this.stats.built++;
    this.emit({ k: 'build', t: this.absTick(), what: kind, storey, col, cost: UNITS[kind].cost });
    return u;
  }

  canBuildRiser(hi: number, col: number): Verdict {
    if (!this.mode.build) return { ok: false, why: 'Not in this mode' };
    if (this.cash < riserCost(hi)) return { ok: false, why: 'Not enough cash' };
    return canPlaceRiser(this.tower, hi, col, this.mode.maxStorey);
  }

  buildRiser(hi: number, col: number): Riser | null {
    if (!this.canBuildRiser(hi, col).ok) return null;
    const r = placeRiser(this.tower, hi, col);
    this.cash -= riserCost(hi);
    this.stats.built++;
    this.emit({ k: 'build', t: this.absTick(), what: 'riser', storey: hi, col, cost: riserCost(hi) });
    return r;
  }

  canExtend(r: Riser, hi: number): Verdict {
    if (!this.mode.build) return { ok: false, why: 'Not in this mode' };
    if (this.cash < extendCost(r, hi)) return { ok: false, why: 'Not enough cash' };
    return canExtendRiser(this.tower, r, hi, this.mode.maxStorey);
  }

  extend(r: Riser, hi: number): boolean {
    if (!this.canExtend(r, hi).ok) return false;
    const cost = extendCost(r, hi);
    extendRiser(this.tower, r, hi);
    this.cash -= cost;
    this.stats.built++;
    this.emit({ k: 'build', t: this.absTick(), what: 'extend', storey: hi, col: r.col, cost });
    return true;
  }

  canBuildStair(storey: number, col: number): Verdict {
    if (!this.mode.build) return { ok: false, why: 'Not in this mode' };
    if (this.cash < STAIR_COST) return { ok: false, why: 'Not enough cash' };
    return canPlaceStair(this.tower, storey, col, this.mode.maxStorey);
  }

  buildStair(storey: number, col: number): boolean {
    if (!this.canBuildStair(storey, col).ok) return false;
    placeStair(this.tower, storey, col);
    this.cash -= STAIR_COST;
    this.stats.built++;
    this.emit({ k: 'build', t: this.absTick(), what: 'stair', storey, col, cost: STAIR_COST });
    return true;
  }

  carSlots(): number {
    return BASE_CAR_SLOTS + this.tower.units.filter((u) => u.kind === 'service').length;
  }

  canBuyCar(r: Riser, express: boolean): Verdict {
    if (!this.mode.build) return { ok: false, why: 'Not in this mode' };
    if (this.cars.length >= this.carSlots()) return { ok: false, why: 'No car slot — build a service room' };
    if (this.cars.length >= MAX_CARS) return { ok: false, why: 'The lot will not carry another car' };
    if (this.cars.filter((c) => c.riser === r.id).length >= MAX_CARS_PER_RISER)
      return { ok: false, why: 'One car per riser — capacity means another riser' };
    if (this.cash < specOf(express).cost) return { ok: false, why: 'Not enough cash' };
    return { ok: true, why: '' };
  }

  buyCar(r: Riser, express: boolean): Car | null {
    if (!this.canBuyCar(r, express).ok) return null;
    this.cash -= specOf(express).cost;
    this.stats.built++;
    this.emit({ k: 'build', t: this.absTick(), what: express ? 'express' : 'car', storey: r.hi, col: r.col, cost: specOf(express).cost });
    return this.installCar(r, express, false);
  }

  /**
   * Cars in a riser are ordered by id and may never pass each other, so a NEW car has to be installed
   * ABOVE the ones already in the shaft — a car materialising at the concourse under a car that has a
   * lower id would be an ordering the physics can never satisfy, and both cars would sit blocked.
   */
  private installCar(r: Riser, express: boolean, opening: boolean): Car {
    const inRiser = this.cars.filter((c) => c.riser === r.id);
    const above = inRiser.length ? Math.max(...inRiser.map(carStorey)) + 1 : CONCOURSE;
    const at = opening ? Math.min(r.hi, inRiser.length) : Math.min(r.hi, above);
    const c = createCar(this.nextCarId++, r.id, express, at);
    this.cars.push(c);
    return c;
  }

  setPolicy(r: Riser, policy: RiserPolicy, expressFrom = r.expressFrom): void {
    if (r.policy === policy && r.expressFrom === expressFrom) return;
    r.policy = policy;
    r.expressFrom = Math.min(STOREYS - 1, Math.max(1, expressFrom));
    this.stats.policyChanges++;
    // A storey this riser no longer serves must not keep a promise made under the old policy.
    for (let s = 0; s < STOREYS; s++) {
      if (serves(r, s)) continue;
      for (const d of [1, -1] as Dir[]) this.hallCar[qi(r.id, s, d)] = -1;
    }
    // A promise made to somebody ALREADY ABOARD survives a policy change. Dropping every unserved
    // storey from a car's stop list sounds obviously right and sealed passengers inside the lift for
    // the rest of the run: a rider bound for storey 6 on a riser that has just been set express from
    // storey 10 had their destination deleted out from under them, the car ran out of reasons to move
    // and went idle for ever, and the landing it had been assigned stayed marked as its job so
    // nothing else was ever sent either. The mechanism audit counted 653 people sealed in across 34
    // of 42 runs; nothing else in the suite noticed, because a stuck car simply looks like a slow one.
    for (const c of this.cars) {
      if (c.riser !== r.id) continue;
      const owed = new Set(c.riders.map((id) => this.agents[id].legTo));
      for (const set of [c.stops.up, c.stops.down]) {
        for (const s of [...set]) if (!serves(r, s) && !owed.has(s)) set.delete(s);
      }
    }
  }

  sellUnit(u: Unit): boolean {
    if (!this.mode.build) return false;
    if (u.let) {
      // An occupied unit is never demolished out from under its people — that is how a simulation
      // loses an agent mid-ride and quietly runs a car at 6/8 for the rest of the game. It is served
      // notice instead, and it empties at the next night boundary.
      if (u.notice) return false;
      u.notice = true;
      this.emit({ k: 'notice', t: this.absTick(), unit: u.id, storey: u.storey });
      return true;
    }
    const refund = Math.round(UNITS[u.kind].cost * SELL_REFUND);
    this.cash += refund;
    this.emit({ k: 'sell', t: this.absTick(), what: u.kind, id: u.id, refund });
    removeUnit(this.tower, u);
    return true;
  }

  /**
   * The player taps a crowd. The nearest eligible car is re-assigned to it at once.
   *
   * A summon that asks for something already about to happen is a WHIFF and locks that riser's
   * controller out for four seconds. Without that, the optimal play is to tap everything constantly,
   * which is not a decision — it is a tremor.
   */
  summon(riser: number, storey: number, dir: Dir): boolean {
    if (this.now < this.lockoutUntil[riser]) return false;
    const q = this.queues[qi(riser, storey, dir)];
    if (!q.length) return false;
    this.stats.summons++;

    const held = this.hallCar[qi(riser, storey, dir)];
    if (held >= 0) {
      const car = this.cars.find((c) => c.id === held);
      if (car && this.etd(car, storey, dir) <= SUMMON_WHIFF_WINDOW_TICKS) {
        this.lockoutUntil[riser] = this.now + SUMMON_LOCKOUT_TICKS;
        this.emit({ k: 'summon', t: this.absTick(), riser, storey, dir, whiff: true });
        return false;
      }
    }
    this.emit({ k: 'summon', t: this.absTick(), riser, storey, dir, whiff: false });
    this.hallCar[qi(riser, storey, dir)] = -1;

    // THE POWER THE CONTROLLER DOES NOT HAVE: turn a car round.
    //
    // Collective control finishes its sweep. Always. It will run a half-empty car to the top of the
    // building to serve one person while fifteen people stand at storey three watching it go, and
    // there is nothing in its rules that can decide the fifteen matter more — its ranking cannot see
    // how many people are waiting, or for how long. A summon abandons the sweep and brings the car
    // back, and it costs exactly what it should: everybody already aboard is carried the wrong way
    // first.
    //
    // This is the second attempt. The first re-ran the same ranking on the same cars and measured a
    // **0.0% improvement over never touching the screen, on all twelve seeds, byte-identical** — the
    // verb the whole game is built around was decoration, and only the negative-GAP arm of the
    // balance sim was ever going to say so.
    const taker = this.preemptCandidate(riser, storey, dir);
    if (taker) this.preempt(taker, storey, dir, riser);
    else this.dispatch(riser, storey, dir, true);

    // The power the automatic controller does not have: SEND A SECOND CAR.
    //
    // The controller sends exactly one car per landing call and its ranking never looks at how many
    // people are standing there — one person and fifteen people are the same call to it. That is a
    // legible limitation rather than a hobble, and it is where the player's judgement lives: a crowd
    // bigger than one car can hold needs two, and only a person watching the tower can see that.
    //
    // This was measured before it was written. With the summon doing nothing but re-running the same
    // ranking, the improvement over never touching the screen was **0.0% across twelve seeds** — the
    // second verb was decoration, and principle #18's negative-GAP arm is the only thing that would
    // ever have said so out loud.
    const first = taker ?? this.cars.find((c) => c.id === this.hallCar[qi(riser, storey, dir)]);
    const spare = first ? first.cap - first.riders.length : 0;
    if (q.length > spare) this.dispatchExtra(riser, storey, dir, first?.id ?? -1);
    return true;
  }

  /**
   * The car a summon should turn round: the one that can physically be here soonest, counting a
   * reversal as merely slow rather than impossible. A full car is never pulled — dragging people who
   * are already aboard away from their floor to pick up more people it has no room for is the one
   * outcome a summon must not be able to produce.
   */
  private preemptCandidate(riser: number, storey: number, dir: Dir): Car | null {
    const r = this.tower.risers.find((x) => x.id === riser);
    if (!r || !serves(r, storey)) return null;
    let best: Car | null = null;
    let bestCost = Infinity;
    for (const c of this.cars) {
      if (c.riser !== riser) continue;
      if ((dir === 1 ? c.stops.up : c.stops.down).has(storey)) return c;
      if (c.riders.length >= c.cap) continue;
      if (c.state === 'run' && !canStopAt(c, storey, c.dir) && (storey - c.posQ / 1000) * c.dir > 0) continue;
      const at = c.posQ / 1000;
      const reversing = (storey - at) * c.dir < 0;
      // A LOADED car is never turned round. Measured: allowing it made a summoning player 4% worse
      // than one who never touched the screen, and 34% worse on one seed — because every reversal
      // spent the patience of everybody already aboard to buy patience for the people on the landing,
      // and that trade is a loss almost every time. What a summon actually redirects is EMPTY
      // capacity: the car going up with nobody in it while a crowd stands three floors below.
      if (reversing && c.riders.length > 0) continue;
      const cost = runTicks(c, Math.abs(storey - at)) + (reversing ? 20 : 0) + c.riders.length * 6;
      if (cost < bestCost || (cost === bestCost && best && c.id < best.id)) {
        bestCost = cost;
        best = c;
      }
    }
    return best;
  }

  private preempt(c: Car, storey: number, dir: Dir, riser: number): void {
    (dir === 1 ? c.stops.up : c.stops.down).add(storey);
    this.hallCar[qi(riser, storey, dir)] = c.id;
    this.emit({ k: 'assign', t: this.absTick(), riser, storey, dir, car: c.id, manual: true });
    const at = c.posQ / 1000;
    if ((storey - at) * c.dir < 0) {
      c.dir = (c.dir === 1 ? -1 : 1) as Dir;
      this.emit({ k: 'reverse', t: this.absTick(), car: c.id, storey: Math.round(at), dir: c.dir });
    }
    if (c.state === 'doors') return;
    this.chooseNextStop(c);
  }

  /** Put a second car on a landing that one car cannot clear. Only ever from a manual summon. */
  private dispatchExtra(riser: number, storey: number, dir: Dir, exclude: number): void {
    const r = this.tower.risers.find((x) => x.id === riser);
    if (!r || !serves(r, storey)) return;
    let best: Car | null = null;
    let bestEtd = Infinity;
    for (const c of this.cars) {
      if (c.riser !== riser || c.id === exclude) continue;
      if (c.riders.length >= c.cap) continue;
      if (c.state === 'run' && !(c.dir === dir && canStopAt(c, storey, dir))) continue;
      if (c.state === 'doors' && c.dir === dir && (storey - c.posQ / 1000) * dir <= 0) continue;
      if ((dir === 1 ? c.stops.up : c.stops.down).has(storey)) return;
      const e = this.etd(c, storey, dir);
      if (e < bestEtd) {
        bestEtd = e;
        best = c;
      }
    }
    if (!best) return;
    (dir === 1 ? best.stops.up : best.stops.down).add(storey);
    this.emit({ k: 'assign', t: this.absTick(), riser, storey, dir, car: best.id, manual: true });
    if (best.state === 'idle' || best.state === 'run') this.chooseNextStop(best);
  }

  // ── the tick ──────────────────────────────────────────────────────────────────────────────────

  absTick(): number {
    return this.now;
  }

  step(): void {
    if (this.over) return;

    this.spawnDue();
    this.stepWalkers();
    this.stepCars();
    if (this.tick % 10 === 0) {
      this.sweepPatience();
      this.redispatch();
    }

    this.tick++;
    this.now++;
    if (this.tick >= DAY_TICKS) this.night();
  }

  private spawnDue(): void {
    while (this.pendingIdx < this.pending.length && this.pending[this.pendingIdx].tick <= this.tick) {
      this.deferred.push(this.pending[this.pendingIdx++]);
    }
    // A trip that cannot find a free agent slot waits its turn rather than vanishing, which reads as
    // a staggered rush instead of a stall and keeps the pool a hard bound rather than a lossy one.
    while (this.deferred.length && this.freeAgents.length) {
      const trip = this.deferred.shift() as PendingTrip;
      this.beginTrip(trip);
    }
  }

  private beginTrip(trip: PendingTrip): void {
    const u = this.tower.units.find((x) => x.id === trip.unit);
    if (!u || !u.let) return;
    const id = this.freeAgents.pop();
    if (id === undefined) return;
    const a = this.agents[id];
    a.live = true;
    a.unit = trip.unit;
    a.storey = trip.from;
    a.finalTo = trip.to;
    a.legTo = trip.to;
    a.waitTicks = 0;
    a.rideTicks = 0;
    a.walkTicks = 0;
    a.car = -1;
    a.riser = -1;
    a.col = trip.from === CONCOURSE ? 0 : u.col + (u.w - 1) / 2;
    this.emit({ k: 'spawn', t: this.absTick(), a: id, unit: trip.unit, from: trip.from, to: trip.to });
    this.plan(a);
  }

  /**
   * Choose the route for the rest of this journey.
   *
   * Either one riser serves both ends, or the journey is two legs with a change at the concourse, and
   * there is never a third case — because every riser starts at the concourse. That constraint is the
   * whole reason there is no graph search in this game.
   */
  private plan(a: Agent): void {
    const from = a.storey;
    const to = a.finalTo;
    if (from === to) {
      this.finishTrip(a);
      return;
    }

    // Stairs, for a hop of one or two storeys with a continuous flight.
    const gap = Math.abs(to - from);
    if (gap <= 2 && this.stairsRun(from, to)) {
      const walk = this.walkCost(a.col, this.stairCol(from, to));
      const cost = walk + gap * STAIR_TICKS_PER_STOREY;
      if (cost < this.bestLiftCost(a, from, to)) {
        a.state = 'stairs';
        a.legTo = to;
        a.fromCol = a.col;
        a.targetCol = this.stairCol(from, to);
        a.walkFrom = this.now;
        a.wake = this.now + cost;
        a.walkTicks += cost;
        this.walking.push(a.id);
        this.emit({ k: 'stairs', t: this.absTick(), a: a.id, from, to });
        return;
      }
    }

    const direct = this.bestRiser(a.col, from, to);
    if (direct) {
      this.walkToRiser(a, direct, to);
      return;
    }
    const leg1 = this.bestRiser(a.col, from, CONCOURSE);
    if (leg1) {
      this.walkToRiser(a, leg1, CONCOURSE);
      return;
    }
    // Nowhere to go from here. Rare by construction (a storey with no service is never let), but a
    // player can extend a riser's express floor out from under a tenancy, and then this is honest.
    this.giveUp(a);
  }

  private stairsRun(from: number, to: number): boolean {
    const lo = Math.min(from, to);
    const hi = Math.max(from, to);
    for (let s = lo; s < hi; s++) if (!stairBetween(this.tower, s, s + 1)) return false;
    return true;
  }

  private stairCol(from: number, to: number): number {
    const lo = Math.min(from, to);
    const st = this.tower.stairs.find((s) => s.storey === lo);
    return st ? st.col : 0;
  }

  private walkCost(fromCol: number, toCol: number): number {
    return Math.max(1, Math.round(Math.abs(fromCol - toCol) * WALK_TICKS_PER_COL));
  }

  private legCost(a: Agent, r: Riser, from: number, to: number): number {
    const dir: Dir = to > from ? 1 : -1;
    const wait = this.ew[qi(r.id, from, dir)] / TICK_S;
    const cars = this.cars.filter((c) => c.riser === r.id);
    if (!cars.length) return Infinity;
    const ride = Math.min(...cars.map((c) => runTicks(c, Math.abs(to - from))));
    return this.walkCost(a.col, boardingCol(r)) + wait + ride;
  }

  private bestRiser(col: number, from: number, to: number): Riser | null {
    let best: Riser | null = null;
    let bestCost = Infinity;
    for (const r of this.tower.risers) {
      if (!serves(r, from) || !serves(r, to)) continue;
      if (!this.cars.some((c) => c.riser === r.id)) continue;
      const dir: Dir = to > from ? 1 : -1;
      const wait = this.ew[qi(r.id, from, dir)] / TICK_S;
      const cars = this.cars.filter((c) => c.riser === r.id);
      const ride = Math.min(...cars.map((c) => runTicks(c, Math.abs(to - from))));
      const cost = this.walkCost(col, boardingCol(r)) + wait + ride;
      if (cost < bestCost) {
        bestCost = cost;
        best = r;
      }
    }
    return best;
  }

  private bestLiftCost(a: Agent, from: number, to: number): number {
    const r = this.bestRiser(a.col, from, to);
    return r ? this.legCost(a, r, from, to) : Infinity;
  }

  private walkToRiser(a: Agent, r: Riser, legTo: number): void {
    a.riser = r.id;
    a.legTo = legTo;
    a.dir = legTo > a.storey ? 1 : -1;
    a.state = 'walk';
    a.fromCol = a.col;
    a.targetCol = boardingCol(r);
    a.walkFrom = this.now;
    const cost = this.walkCost(a.col, a.targetCol);
    a.walkTicks += cost;
    a.wake = this.now + cost;
    this.walking.push(a.id);
  }

  private stepWalkers(): void {
    for (let i = this.walking.length - 1; i >= 0; i--) {
      const a = this.agents[this.walking[i]];
      if (!a.live || this.now < a.wake) continue;
      this.walking.splice(i, 1);
      a.col = a.targetCol;
      if (a.state === 'stairs') {
        a.storey = a.legTo;
        this.finishTrip(a);
      } else if (a.state === 'walk' && a.riser >= 0) {
        this.joinQueue(a);
      } else {
        this.finishTrip(a);
      }
    }
  }

  private joinQueue(a: Agent): void {
    a.state = 'wait';
    a.waitStart = this.now;
    const q = this.queues[qi(a.riser, a.storey, a.dir)];
    q.push(a.id);
    this.emit({ k: 'call', t: this.absTick(), a: a.id, riser: a.riser, storey: a.storey, dir: a.dir });
    this.dispatch(a.riser, a.storey, a.dir, false);
  }

  // ── dispatch ──────────────────────────────────────────────────────────────────────────────────

  /** Rough ticks before this car could open its doors at `storey` heading `dir`. */
  private etd(c: Car, storey: number, dir: Dir): number {
    const at = c.posQ / 1000;
    let n = 0;
    const set = dir === 1 ? c.stops.up : c.stops.down;
    for (const s of set) if ((s - at) * dir > 0 && (storey - s) * dir > 0) n++;
    let t = runTicks(c, Math.abs(storey - at)) + ETD_STOP_PENALTY * n;
    if (c.riders.length >= c.cap) t += ETD_FULL_PENALTY;
    t += this.zonePenalty(c, storey);
    return t;
  }

  /**
   * Two cars in one shaft are given HALVES of the building to prefer.
   *
   * Without this, a purely nearest-car ranking sends both of them at whatever call happens to be
   * closest, so they converge, and since they cannot pass each other the one behind spends the peak
   * stuck. Measured over eight seeded runs, buying a second car made the tower WORSE — 7,519
   * person-seconds of waiting became 11,196. A player who spends £1,600 and watches their building
   * get slower has been sold a trap, and no outcome metric would ever have said so: it just looks
   * like the game is hard.
   *
   * Zoning is what real multi-car controllers do, it is a soft preference rather than a rule (a car
   * still answers the other half when nothing else can), and it leaves bunching visible when both
   * cars are genuinely busy — which is the honest version of the mechanic.
   */
  private zonePenalty(c: Car, storey: number): number {
    const mine = this.carsIn(c.riser);
    if (mine.length < 2) return 0;
    const r = this.tower.risers.find((x) => x.id === c.riser);
    if (!r) return 0;
    const k = mine.findIndex((x) => x.id === c.id);
    const span = r.hi - r.lo;
    const lo = r.lo + Math.floor((span * k) / mine.length);
    const hi = r.lo + Math.ceil((span * (k + 1)) / mine.length);
    if (storey >= lo && storey <= hi) return 0;
    // The concourse is everybody's business — it is where every journey starts or ends.
    if (storey === CONCOURSE) return 0;
    return ETD_ZONE_PENALTY;
  }

  /**
   * Give a landing call to a car — but ONLY to a car that can actually keep the promise.
   *
   * A car already inside its own braking distance for that storey is not a candidate, even if it is
   * the nearest thing in the shaft. That single refusal is what lets the mechanism audit ask "did any
   * car ever go past a stop it had promised" without knowing a thing about kinematics: if the answer
   * is ever yes, the dispatcher promised what it could not deliver, and there is no grey zone to
   * argue about. An idle car is always a candidate, because it is free to reposition.
   */
  private dispatch(riser: number, storey: number, dir: Dir, manual: boolean): void {
    const key = qi(riser, storey, dir);
    if (!manual && this.hallCar[key] >= 0) return;
    if (!this.queues[key].length) return;

    let best: Car | null = null;
    let bestEtd = Infinity;
    for (const c of this.cars) {
      if (c.riser !== riser) continue;
      const r = this.tower.risers.find((x) => x.id === riser);
      if (!r || !serves(r, storey)) continue;
      // A FULL car is not a candidate at any price.
      //
      // The capacity term used to be a soft penalty on the ranking, and a full car still won often
      // enough to matter: it was sent to landing after landing where nobody could get in, opened its
      // doors on each one, and never got far enough up the building to let anybody out. Its twelve
      // passengers rode round for EIGHT SHIFTS. Every other invariant was clean the whole time —
      // the car moved, answered its calls and stopped where it had promised — and the tower simply
      // read as slow. A car with nobody able to board is not a car that can answer a call.
      if (c.riders.length >= c.cap) continue;
      if (c.state === 'run' && !(c.dir === dir && canStopAt(c, storey, dir))) continue;
      if (c.state === 'doors' && c.dir === dir && (storey - c.posQ / 1000) * dir <= 0) continue;
      const e = this.etd(c, storey, dir);
      if (e < bestEtd || (e === bestEtd && best && c.id < best.id)) {
        bestEtd = e;
        best = c;
      }
    }
    if (!best) return;

    const prev = this.hallCar[key];
    if (prev >= 0 && prev !== best.id) {
      const old = this.cars.find((c) => c.id === prev);
      // Taking a LANDING call off a car must never take away a stop somebody ABOARD it is riding to.
      // Without this guard, handing storey 6's call to a nearer car deleted storey 6 from the old
      // car's list while a passenger was sitting in it bound for storey 6 — and that passenger then
      // rode round the building for the rest of the game. Twenty-four people were still in the lifts
      // when the run ended, the oldest having boarded on day two. Every other invariant was clean:
      // the car was moving, answering calls, and stopping where it had promised. It had simply
      // stopped promising the one thing it was carrying.
      if (old && !old.riders.some((id) => this.agents[id].legTo === storey)) {
        (dir === 1 ? old.stops.up : old.stops.down).delete(storey);
      }
    }
    this.hallCar[key] = best.id;
    (dir === 1 ? best.stops.up : best.stops.down).add(storey);
    this.emit({ k: 'assign', t: this.absTick(), riser, storey, dir, car: best.id, manual });
    // Re-plan the car AT ONCE, even mid-run.
    //
    // Without this, a stop handed to a car already travelling was simply added to a set nobody looked
    // at again until the car reached wherever it was originally going — so a car running from the
    // concourse to storey 12 that was given storey 5 on the way FLEW STRAIGHT PAST STOREY 5 with
    // that landing's call in its own promise list. It collected them on the next sweep, which shows
    // up as nothing worse than a slightly higher average wait, and is exactly the class of defect an
    // outcome metric cannot see. It is also why the summon verb measured a 0.0% improvement: the
    // player's tap re-assigned the same car and then changed nothing about what it did next.
    if (best.state === 'idle' || best.state === 'run') this.chooseNextStop(best);
  }

  /** Calls nobody could take when they were made get another look every tower-second. */
  private redispatch(): void {
    for (let r = 0; r < this.tower.risers.length; r++) {
      for (let s = 0; s < STOREYS; s++) {
        for (const d of [1, -1] as Dir[]) {
          const key = qi(r, s, d);
          if (!this.queues[key].length) continue;
          const held = this.hallCar[key];
          const holder = held >= 0 ? this.cars.find((c) => c.id === held) : undefined;
          // "Assigned" is not the same as "still promised": a car that served the other direction at
          // this landing, or had its stop dropped by a policy change, is no longer coming.
          if (holder && (d === 1 ? holder.stops.up : holder.stops.down).has(s)) continue;
          this.hallCar[key] = -1;
          this.dispatch(r, s, d, false);
        }
      }
    }
  }

  // ── cars ──────────────────────────────────────────────────────────────────────────────────────

  private stepCars(): void {
    for (const c of this.cars) {
      if (c.state === 'doors') {
        if (this.now >= c.doorUntil) this.closeDoors(c);
        continue;
      }
      if (c.state === 'idle') {
        this.chooseNextStop(c);
        if (c.state === 'idle' && this.now - c.idleSince > PARK_AFTER_TICKS) {
          const home = this.parkStorey(c);
          const { lo, hi } = travelBounds(c, this.carsIn(c.riser));
          if (carStorey(c) !== home && home >= lo && home <= hi) this.commit(c, home);
          else c.idleSince = this.now;
        }
        continue;
      }
      const arrived = stepCar(c, this.passBuf);
      for (const s of this.passBuf) {
        this.emit({
          k: 'pass',
          t: this.absTick(),
          car: c.id,
          riser: c.riser,
          storey: s,
          dir: c.dir,
          occ: c.riders.length,
          cap: c.cap,
        });
      }
      if (arrived) this.openDoors(c);
    }
  }

  /**
   * The collective-control rule: keep going until nothing is left ahead, then turn round.
   *
   * Two things here are scar tissue and must not be tidied away.
   *
   * FIRST, a promise at the storey the car is ALREADY STANDING ON is served now. Every "next stop"
   * test is `(s - at) * dir > 0`, which is strictly ahead — so a car that stopped at storey 5 going
   * up, while somebody at storey 5 was still waiting to go down, could never select storey 5 again.
   * It sat there flipping its direction every tick, for ever, while the landing stayed marked as
   * assigned to it so nothing else was ever sent. One deadlocked car, one dead tower, 1,500 reverse
   * events and no error anywhere.
   *
   * SECOND, the direction only flips when a flip actually produces a run. The version that flipped
   * first and looked afterwards emitted a reverse on every tick a stuck car did nothing.
   */
  private chooseNextStop(c: Car): void {
    // A LIFT ALWAYS KNOWS WHERE ITS PASSENGERS ARE GOING. Every time this car decides what to do
    // next, the destinations of everybody aboard are put back on its list first.
    //
    // This is an invariant rather than a repair. A rider's stop can be removed by several perfectly
    // reasonable-looking pieces of code — a landing call reassigned to a nearer car, a riser's policy
    // changed, a queue cancelled when its last person gave up — and each of those was written by
    // somebody thinking about LANDING calls, not about the people already inside the box. The result
    // was passengers riding round the building for eight shifts while the car went on answering calls
    // perfectly correctly. Rather than guard each deletion and hope the next one remembers, the car
    // simply re-derives what it owes its own passengers, every time it chooses.
    for (const id of c.riders) {
      const a = this.agents[id];
      if (a.legTo === carStorey(c)) continue;
      (a.legTo > c.posQ / 1000 ? c.stops.up : c.stops.down).add(a.legTo);
    }
    const at = c.posQ / 1000;
    const here = Math.round(at);
    if (
      Math.abs(at - here) < 1e-9 &&
      (c.stops.up.has(here) || c.stops.down.has(here)) &&
      (c.riders.length < c.cap || c.riders.some((id) => this.agents[id].legTo === here))
    ) {
      c.blockedOn = -1;
      this.openDoors(c);
      return;
    }

    const { lo, hi } = travelBounds(c, this.carsIn(c.riser));
    let denied = -1;

    // A FULL LIFT RUNS NON-STOP. When there is no room for anybody else, the only stops that mean
    // anything are the ones its passengers asked for, and a landing call is not a thing it can answer.
    //
    // Excluding full cars from dispatch was necessary and not sufficient: a car can fill up AFTER it
    // was assigned, and then it spends its time opening its doors on floors where nobody can get in
    // while the people inside it ride past their own. This makes the state unreachable rather than
    // merely rarer, and it is also just what a lift does.
    const full = c.riders.length >= c.cap;
    const wanted = full ? new Set(c.riders.map((id) => this.agents[id].legTo)) : null;
    const allowed = (storey: number): boolean => !wanted || wanted.has(storey);

    const pick = (dir: Dir): number => {
      const same = dir === 1 ? c.stops.up : c.stops.down;
      const opp = dir === 1 ? c.stops.down : c.stops.up;
      let best = -1;
      for (const s of same) {
        if ((s - at) * dir <= 0 || !allowed(s)) continue;
        if (s < lo || s > hi) {
          denied = s;
          continue;
        }
        if (best < 0 || (s - best) * dir < 0) best = s;
      }
      if (best >= 0) return best;
      // Nothing to serve ahead in this direction, but a call in the OTHER direction may be further
      // on — a car runs up empty to the highest down-call, then turns round. That is the sweep.
      for (const s of opp) {
        if ((s - at) * dir <= 0 || !allowed(s)) continue;
        if (s < lo || s > hi) {
          denied = s;
          continue;
        }
        if (best < 0 || (s - best) * dir > 0) best = s;
      }
      return best;
    };

    let next = pick(c.dir);
    if (next < 0) {
      const other = pick((c.dir === 1 ? -1 : 1) as Dir);
      if (other >= 0) {
        c.dir = (c.dir === 1 ? -1 : 1) as Dir;
        this.emit({ k: 'reverse', t: this.absTick(), car: c.id, storey: here, dir: c.dir });
        next = other;
      }
    }
    if (next >= 0) {
      this.commit(c, next);
      return;
    }
    if (denied >= 0) {
      this.yieldFor(c, denied);
      if (c.blockedOn !== denied) {
        c.blockedOn = denied;
        this.emit({ k: 'blocked', t: this.absTick(), car: c.id, riser: c.riser, wanted: denied });
      }
    }
    if (c.state !== 'idle') {
      c.state = 'idle';
      c.idleSince = this.now;
      c.target = -1;
    }
  }

  private carsIn(riser: number): Car[] {
    return this.cars.filter((x) => x.riser === riser);
  }

  /**
   * Where a car with nothing to do waits: the bottom car goes home to the concourse, the top car goes
   * to the top of the shaft. Opposite ends, never the middle.
   *
   * A first version spread them evenly up the riser and it produced a deadlock that killed every run
   * at the same point. Two cars, one parked at storey 4. Morning peak: the lower car fills at the
   * concourse with people going to storeys 6, 7 and 8, and cannot pass the parked one, so it can only
   * reach storey 4 — while the parked car sits there with nothing to do, because nobody is standing
   * on storey 4. The building's whole upper half became unreachable at exactly the moment it was
   * busiest, and nothing in the game said so: the tower just started dying on day five.
   */
  private parkStorey(c: Car): number {
    const r = this.tower.risers.find((x) => x.id === c.riser);
    if (!r) return CONCOURSE;
    const mine = this.carsIn(c.riser);
    const k = mine.findIndex((x) => x.id === c.id);
    return mine.length <= 1 || k === 0 ? r.lo : r.hi;
  }

  /**
   * An idle car with nothing to do never stands in another car's way — it clears out to the far end.
   *
   * This is the other half of the deadlock fix. Parking at opposite ends stops the common case; this
   * stops the rest, because a car can also fall idle anywhere in the shaft after its last drop-off.
   * A car that is actually WORKING still blocks, which is what keeps bunching a real mechanic rather
   * than an exception the code quietly removed.
   */
  private yieldFor(c: Car, wanted: number): void {
    const r = this.tower.risers.find((x) => x.id === c.riser);
    if (!r) return;
    const at = c.posQ / 1000;
    for (const o of this.carsIn(c.riser)) {
      if (o.id === c.id || o.state !== 'idle') continue;
      if (o.stops.up.size + o.stops.down.size > 0) continue;
      const oAt = o.posQ / 1000;
      const between = (oAt - at) * Math.sign(wanted - at) > 0 && (wanted - oAt) * Math.sign(wanted - at) >= 0;
      if (!between) continue;
      const away = o.id > c.id ? r.hi : r.lo;
      if (Math.abs(away - oAt) < 1e-9) continue;
      const b = travelBounds(o, this.carsIn(o.riser));
      if (away >= b.lo && away <= b.hi) this.commit(o, away);
    }
  }

  private commit(c: Car, storey: number): void {
    const at = c.posQ / 1000;
    if (c.state === 'run' && c.target === storey) return;
    c.blockedOn = -1;
    if (Math.abs(storey - at) < 1e-9) {
      this.openDoors(c);
      return;
    }
    c.dir = (storey > at ? 1 : -1) as Dir;
    c.target = storey;
    c.state = 'run';
    this.emit({ k: 'move', t: this.absTick(), car: c.id, riser: c.riser, from: Math.round(at), to: storey, dir: c.dir });
  }

  private openDoors(c: Car): void {
    const s = carStorey(c);
    // Arriving at a storey that only has a call in the other direction means the sweep turns here.
    const sameSet = c.dir === 1 ? c.stops.up : c.stops.down;
    const oppSet = c.dir === 1 ? c.stops.down : c.stops.up;
    if (!sameSet.has(s) && oppSet.has(s)) {
      c.dir = (c.dir === 1 ? -1 : 1) as Dir;
      this.emit({ k: 'reverse', t: this.absTick(), car: c.id, storey: s, dir: c.dir });
    }
    c.lastDoor = this.now;
    this.emit({
      k: 'arrive',
      t: this.absTick(),
      car: c.id,
      riser: c.riser,
      storey: s,
      dir: c.dir,
      occ: c.riders.length,
      cap: c.cap,
    });
    // Only the direction being SERVED is discharged. Deleting both dropped the opposite direction's
    // promise while `hallCar` still named this car, so `redispatch` skipped it forever and everybody
    // waiting to go the other way from that landing stood there until they gave up.
    (c.dir === 1 ? c.stops.up : c.stops.down).delete(s);

    let moved = 0;
    for (let i = c.riders.length - 1; i >= 0; i--) {
      const a = this.agents[c.riders[i]];
      if (a.legTo !== s) continue;
      c.riders.splice(i, 1);
      moved++;
      a.rideTicks += this.now - a.rideStart;
      a.storey = s;
      a.car = -1;
      this.storeyFootfall[s]++;
      this.emit({ k: 'alight', t: this.absTick(), a: a.id, car: c.id, storey: s });
      this.afterLeg(a);
    }

    const q = this.queues[qi(c.riser, s, c.dir)];
    let boarded = 0;
    while (q.length && c.riders.length < c.cap) {
      const a = this.agents[q.shift() as number];
      if (!a.live || a.state !== 'wait') continue;
      a.state = 'ride';
      a.car = c.id;
      a.rideStart = this.now;
      a.waitTicks += this.now - a.waitStart;
      this.noteWait(a.riser, s, c.dir, this.now - a.waitStart);
      c.riders.push(a.id);
      (c.dir === 1 ? c.stops.up : c.stops.down).add(a.legTo);
      boarded++;
      moved++;
      this.storeyFootfall[s]++;
      this.emit({ k: 'board', t: this.absTick(), a: a.id, car: c.id, storey: s, dir: c.dir, to: a.legTo });
    }
    this.hallCar[qi(c.riser, s, c.dir)] = -1;

    c.state = 'doors';
    c.doorUntil = this.now + dwellTicks(c, moved);
    c.pendingBoarded = boarded;
  }

  private closeDoors(c: Car): void {
    const s = carStorey(c);
    const left = this.queues[qi(c.riser, s, c.dir)].length;
    this.emit({
      k: 'depart',
      t: this.absTick(),
      car: c.id,
      riser: c.riser,
      storey: s,
      dir: c.dir,
      occ: c.riders.length,
      boarded: c.pendingBoarded ?? 0,
      leftBehind: left,
    });
    c.pendingBoarded = 0;
    // Anyone still standing there needs a car again, and this is the moment to ask for one.
    if (left > 0) {
      this.hallCar[qi(c.riser, s, c.dir)] = -1;
      this.dispatch(c.riser, s, c.dir, false);
    }
    c.state = 'idle';
    c.idleSince = this.now;
    c.target = -1;
    this.chooseNextStop(c);
  }

  private afterLeg(a: Agent): void {
    if (a.storey === a.finalTo) {
      const u = this.tower.units.find((x) => x.id === a.unit);
      const destCol = a.finalTo === CONCOURSE || !u ? 0 : u.col + (u.w - 1) / 2;
      a.state = 'walk';
      a.riser = -1;
      a.fromCol = a.col;
      a.targetCol = destCol;
      a.walkFrom = this.now;
      const cost = this.walkCost(a.col, destCol);
      a.walkTicks += cost;
      a.wake = this.now + cost;
      this.walking.push(a.id);
      return;
    }
    this.plan(a);
  }

  private finishTrip(a: Agent): void {
    const u = this.tower.units.find((x) => x.id === a.unit);
    this.emit({
      k: 'trip',
      t: this.absTick(),
      a: a.id,
      unit: a.unit,
      from: a.storey,
      to: a.finalTo,
      waitTicks: a.waitTicks,
      rideTicks: a.rideTicks,
      walkTicks: a.walkTicks,
    });
    this.stats.trips++;
    this.stats.waitTicks += a.waitTicks;
    this.stats.personSecondsLost += a.waitTicks * TICK_S;
    if (u && u.let) {
      const waitS = a.waitTicks * TICK_S;
      const delta = clamp((GOOD_WAIT_S - waitS) / GOOD_WAIT_S, -1, 1) * SAT_PER_TRIP;
      u.sat = clamp(u.sat + delta, 0.02, 1);
      u.tripsToday++;
      if (waitS > GOOD_WAIT_S) u.lateTripsToday++;
    }
    this.release(a);
  }

  private giveUp(a: Agent): void {
    const u = this.tower.units.find((x) => x.id === a.unit);
    if (u && u.let) u.sat = clamp(u.sat - SAT_PER_GIVEUP, 0.02, 1);
    this.stats.giveUps++;
    this.stats.personSecondsLost += a.waitTicks * TICK_S;
    this.emit({ k: 'giveup', t: this.absTick(), a: a.id, storey: a.storey, waitTicks: a.waitTicks });
    this.release(a);
  }

  private release(a: Agent): void {
    this.emit({ k: 'despawn', t: this.absTick(), a: a.id });
    a.live = false;
    a.car = -1;
    a.riser = -1;
    this.freeAgents.push(a.id);
  }

  private sweepPatience(): void {
    for (const a of this.agents) {
      if (!a.live || a.state !== 'wait') continue;
      const waited = this.now - a.waitStart;
      if (waited < this.mode.giveUpTicks) continue;
      const q = this.queues[qi(a.riser, a.storey, a.dir)];
      const i = q.indexOf(a.id);
      if (i >= 0) q.splice(i, 1);
      a.waitTicks += waited;
      this.noteWait(a.riser, a.storey, a.dir, waited);
      const riser = a.riser;
      const storey = a.storey;
      const dir = a.dir;
      this.giveUp(a);
      if (!q.length) this.cancelCall(riser, storey, dir);
    }
  }

  /**
   * The last person waiting at a landing has gone. Take the promise back.
   *
   * Without this the car still travels to a landing nobody is standing on, opens its doors on an
   * empty floor, and closes them again — and in a tower under real pressure that was FIVE HUNDRED of
   * seven hundred door cycles spent on people who had already walked out. Capacity spent on ghosts is
   * capacity the people still queueing never get, so a give-up quietly makes the next give-up more
   * likely. Nothing about it looks like a bug from the outside; the tower simply performs worse.
   */
  private cancelCall(riser: number, storey: number, dir: Dir): void {
    const key = qi(riser, storey, dir);
    const held = this.hallCar[key];
    if (held < 0) return;
    this.hallCar[key] = -1;
    const car = this.cars.find((c) => c.id === held);
    if (!car) return;
    // A rider aboard may still be going there — only a promise nobody is riding on can be dropped.
    if (car.riders.some((id) => this.agentLegTo(id) === storey)) return;
    (dir === 1 ? car.stops.up : car.stops.down).delete(storey);
  }

  private agentLegTo(id: number): number {
    return this.agents[id].legTo;
  }

  private noteWait(riser: number, storey: number, dir: Dir, ticks: number): void {
    const key = qi(riser, storey, dir);
    const seconds = ticks * TICK_S;
    this.ew[key] = clamp(this.ew[key] + EW_ALPHA * (seconds - this.ew[key]), EW_MIN_S, EW_MAX_S);
    this.storeyWaitSum[storey] += seconds;
    this.storeyWaitCount[storey]++;
  }

  // ── the night boundary: the books ─────────────────────────────────────────────────────────────

  private night(): void {
    const t = this.absTick();
    let income = 0;

    const shopsOn = new Int32Array(STOREYS);
    for (const u of this.tower.units) if (u.kind === 'shop' && u.let) shopsOn[u.storey]++;

    for (const u of this.tower.units) {
      if (!u.let) {
        u.vacantDays++;
        continue;
      }
      let pence: number;
      if (u.kind === 'shop') {
        u.footfallToday = shopsOn[u.storey] ? this.storeyFootfall[u.storey] / shopsOn[u.storey] : 0;
        pence = Math.round(SHOP_PER_FOOTFALL * u.footfallToday * 100);
        this.emit({ k: 'footfall', t, unit: u.id, count: u.footfallToday });
      } else {
        const base = UNITS[u.kind].rent * (1 + STOREY_PREMIUM * u.storey);
        pence = Math.round(base * clamp(0.5 + 0.7 * u.sat, RENT_SAT_FLOOR, RENT_SAT_CEIL) * 100);
      }
      income += pence / 100;
      this.emit({ k: 'rent', t, unit: u.id, pence });
    }

    const upkeep = this.upkeep();
    this.cash += income - upkeep;

    this.settleTenancies(t);
    this.relet(t);

    this.rating = this.computeRating();
    this.stats.bestRating = Math.max(this.stats.bestRating, this.rating);
    const pop = this.population();
    this.stats.peakPop = Math.max(this.stats.peakPop, pop);

    let worst = 0;
    let worstS = 0;
    for (let s = 0; s < STOREYS; s++) {
      if (!this.storeyWaitCount[s]) continue;
      const mean = this.storeyWaitSum[s] / this.storeyWaitCount[s];
      if (mean > worst) {
        worst = mean;
        worstS = s;
      }
    }
    if (worst > this.stats.worstStoreyWaitS) {
      this.stats.worstStoreyWaitS = worst;
      this.stats.worstStorey = worstS;
    }

    const totalWait = sum(this.storeyWaitSum);
    const totalCount = sumInt(this.storeyWaitCount);
    this.history.push({
      day: this.day,
      cash: Math.round(this.cash),
      rating: this.rating,
      pop,
      income: Math.round(income),
      upkeep: Math.round(upkeep),
      meanWaitS: totalCount ? totalWait / totalCount : 0,
    });
    this.emit({ k: 'day', t, day: this.day, cash: Math.round(this.cash), rating: this.rating, pop });

    this.bustDays = this.cash < 0 ? this.bustDays + 1 : 0;

    for (const u of this.tower.units) {
      u.tripsToday = 0;
      u.lateTripsToday = 0;
    }
    // A landing nobody used today slowly forgets how bad it was.
    //
    // `ew` only moves when somebody actually completes a wait there, so a storey that got so bad that
    // every tenant walked out recorded no further measurements and kept its terrible average FOR
    // EVER — which meant the re-let rule locked that floor empty permanently and the tower could
    // never climb back. The cascade needs a floor to fall to, not a hole to fall through.
    for (let r = 0; r < this.tower.risers.length; r++) {
      for (let st = 0; st < STOREYS; st++) {
        if (this.storeyWaitCount[st] > 0) continue;
        for (const d of [1, -1] as Dir[]) {
          const k = qi(r, st, d);
          this.ew[k] = clamp(this.ew[k] + EW_FORGET * (EW_INIT_S - this.ew[k]), EW_MIN_S, EW_MAX_S);
        }
      }
    }

    this.storeyWaitSum.fill(0);
    this.storeyWaitCount.fill(0);
    this.storeyFootfall.fill(0);

    this.day++;
    this.tick = 0;
    this.pendingIdx = 0;
    this.deferred.length = 0;

    if (this.bustDays >= BUST_DAYS) {
      this.finish(false, 'The bank called it in. Three days in the red is three days too many.');
      return;
    }
    if (this.day > this.mode.days) {
      this.judge();
      return;
    }
    this.scheduleDay();
  }

  upkeep(): number {
    let k = this.mode.dailyCharge;
    for (const u of this.tower.units) k += UNITS[u.kind].upkeep;
    for (const r of this.tower.risers) k += riserUpkeep(r);
    for (const c of this.cars) k += specOf(c.express).upkeep;
    k += this.tower.stairs.length * STAIR_UPKEEP;
    return k;
  }

  private settleTenancies(t: number): void {
    for (const u of [...this.tower.units]) {
      if (!u.let) continue;
      if (u.notice) {
        this.emit({ k: 'leave', t, unit: u.id, storey: u.storey, pop: u.pop });
        this.stats.leaves++;
        u.let = false;
        u.notice = false;
        u.pop = 0;
        u.sat = 0;
        // The cooling-off is what turns churn into a cascade: the unit does not simply refill.
        u.vacantDays = 0;
        continue;
      }
      if (u.sat < SAT_NOTICE) {
        u.notice = true;
        this.emit({ k: 'notice', t, unit: u.id, storey: u.storey });
      }
    }
  }

  private relet(t: number): void {
    const served = servedStoreys(this.tower);
    for (const u of this.tower.units) {
      if (u.let || u.vacantDays < RELET_AFTER_DAYS) continue;
      if (!served[u.storey]) continue;
      if (!this.cars.length) continue;
      // The measured history, not the day just gone. Reading the day's mean let a storey so badly
      // served that everybody had already walked out re-let freely, because a storey nobody travels
      // from records no waits at all — so the worst floors in the building churned back to full
      // occupancy every night and the tower's mood went UP as it fell apart. `ew` is the landing's
      // own exponential average and it survives the day that emptied the floor.
      if (this.landingWait(u.storey) > RELET_MAX_WAIT_S) continue;
      this.letUnit(u, SAT_START, true, t);
    }
  }

  /** The best wait anybody could expect on this storey, across every riser that stops there. */
  landingWait(storey: number): number {
    let best = Infinity;
    for (const r of this.tower.risers) {
      if (!serves(r, storey)) continue;
      if (!this.cars.some((c) => c.riser === r.id)) continue;
      for (const d of [1, -1] as Dir[]) best = Math.min(best, this.ew[qi(r.id, storey, d)]);
    }
    return best;
  }

  private letUnit(u: Unit, sat: number, announce: boolean, t = 0): void {
    u.let = true;
    u.sat = sat;
    u.pop = UNITS[u.kind].pop;
    u.vacantDays = 0;
    u.notice = false;
    if (announce) this.emit({ k: 'let', t, unit: u.id, storey: u.storey, pop: u.pop });
  }

  population(): number {
    let p = 0;
    for (const u of this.tower.units) if (u.let) p += u.pop;
    return p;
  }

  meanSat(): number {
    const letUnits = this.tower.units.filter((u) => u.let);
    if (!letUnits.length) return 0;
    return letUnits.reduce((a, u) => a + u.sat, 0) / letUnits.length;
  }

  occupancy(): number {
    if (!this.tower.units.length) return 0;
    let cap = 0;
    let got = 0;
    for (const u of this.tower.units) {
      cap += UNITS[u.kind].pop;
      if (u.let) got += u.pop;
    }
    return cap ? got / cap : 0;
  }

  computeRating(): number {
    const size = Math.min(1, this.population() / RATING_POP);
    return Math.round(
      1000 * (RATING_SAT_WEIGHT * this.meanSat() + RATING_OCC_WEIGHT * this.occupancy() + RATING_SIZE_WEIGHT * size),
    );
  }

  private judge(): void {
    const g = this.mode.goal;
    const pop = this.population();
    let ok = true;
    if (g.rating !== undefined && this.rating < g.rating) ok = false;
    if (g.pop !== undefined && pop < g.pop) ok = false;
    if (g.meanSat !== undefined && this.meanSat() < g.meanSat) ok = false;
    if (g.popFraction !== undefined && pop < this.startPop * g.popFraction) ok = false;
    this.finish(
      ok,
      ok
        ? 'The building runs. Every storey served, every tenancy staying.'
        : 'The tower stands, but it did not get where it needed to be.',
    );
  }

  private finish(won: boolean, ending: string): void {
    this.over = true;
    this.won = won;
    this.ending = ending;
  }

  /** Ends the run where it stands — the player walking away from a tower that is clearly finished. */
  concede(): void {
    if (!this.over) this.judge();
  }

  // ── the day's traffic ─────────────────────────────────────────────────────────────────────────
  //
  // Demand is STATELESS AND POSITIONAL. Who travels and when is a hash of (seed, day, unit, index) —
  // never a draw from a stream — so building an extra office cannot shift anybody else's arrival, and
  // two players on the same seed face byte-identical traffic no matter what they built. That is the
  // entire async-sharing promise, and `tests/balance.test.ts` asserts it against two runs with
  // completely different action logs.

  private scheduleDay(): void {
    this.pending.length = 0;
    const shopStoreys = this.tower.units.filter((u) => u.kind === 'shop' && u.let).map((u) => u.storey);

    for (const u of this.tower.units) {
      if (!u.let) continue;
      const resident = u.kind === 'flat';
      for (let j = 0; j < u.pop; j++) {
        const upTick = hashRange(PHASE_MID, this.seed, this.day, u.id, j, 1);
        const downTick = PHASE_DOWN + hashRange(PHASE_NIGHT - PHASE_DOWN, this.seed, this.day, u.id, j, 2);
        if (resident) {
          this.pending.push({ tick: upTick, unit: u.id, from: u.storey, to: CONCOURSE });
          this.pending.push({ tick: downTick, unit: u.id, from: CONCOURSE, to: u.storey });
        } else {
          this.pending.push({ tick: upTick, unit: u.id, from: CONCOURSE, to: u.storey });
          this.pending.push({ tick: downTick, unit: u.id, from: u.storey, to: CONCOURSE });
        }
        if (!shopStoreys.length) continue;
        if (hashUnit(this.seed, this.day, u.id, j, 3) >= ERRAND_SHARE) continue;
        let target = -1;
        let bestGap = Infinity;
        for (const s of shopStoreys) {
          const gap = Math.abs(s - u.storey);
          if (gap === 0 || gap > ERRAND_REACH) continue;
          if (gap < bestGap) {
            bestGap = gap;
            target = s;
          }
        }
        if (target < 0) continue;
        const out = PHASE_MID + hashRange(PHASE_DOWN - PHASE_MID - 400, this.seed, this.day, u.id, j, 4);
        this.pending.push({ tick: out, unit: u.id, from: u.storey, to: target });
        this.pending.push({ tick: out + 200 + hashRange(180, this.seed, this.day, u.id, j), unit: u.id, from: target, to: u.storey });
      }
    }
    this.pending.sort((a, b) => a.tick - b.tick || a.unit - b.unit || a.from - b.from);
    this.pendingIdx = 0;
  }

  // ── views for the renderer and the HUD ────────────────────────────────────────────────────────

  /** Everybody currently on the move, with an interpolated position for the renderer. */
  liveAgents(): Array<{ id: number; storey: number; col: number; state: AgentState; waitTicks: number; car: number }> {
    const out = [];
    for (const a of this.agents) {
      if (!a.live) continue;
      let col = a.col;
      if (a.state === 'walk' || a.state === 'stairs') {
        const total = Math.max(1, a.wake - a.walkFrom);
        const p = clamp((this.now - a.walkFrom) / total, 0, 1);
        col = a.fromCol + (a.targetCol - a.fromCol) * p;
      }
      out.push({
        id: a.id,
        storey: a.storey,
        col,
        state: a.state,
        waitTicks: a.state === 'wait' ? this.now - a.waitStart : 0,
        car: a.car,
      });
    }
    return out;
  }

  /** How many people are standing on each storey waiting for each riser, and the worst wait there. */
  queueAt(riser: number, storey: number, dir: Dir): { n: number; worstTicks: number } {
    const q = this.queues[qi(riser, storey, dir)];
    let worst = 0;
    for (const id of q) worst = Math.max(worst, this.now - this.agents[id].waitStart);
    return { n: q.length, worstTicks: worst };
  }

  /** The worst wait anybody is currently enduring on a storey, across every riser and direction. */
  storeyPressure(storey: number): { n: number; worstTicks: number } {
    let n = 0;
    let worst = 0;
    for (let r = 0; r < this.tower.risers.length; r++) {
      for (const d of [1, -1] as Dir[]) {
        const q = this.queueAt(r, storey, d);
        n += q.n;
        worst = Math.max(worst, q.worstTicks);
      }
    }
    return { n, worstTicks: worst };
  }

  /** The crowd a tap on this storey would rescue: the longest-waiting queue there. */
  worstQueueOn(storey: number): { riser: number; dir: Dir } | null {
    let best: { riser: number; dir: Dir } | null = null;
    let worst = -1;
    for (let r = 0; r < this.tower.risers.length; r++) {
      for (const d of [1, -1] as Dir[]) {
        const q = this.queueAt(r, storey, d);
        if (q.n === 0) continue;
        const score = q.worstTicks * 1000 + q.n;
        if (score > worst) {
          worst = score;
          best = { riser: r, dir: d };
        }
      }
    }
    return best;
  }

  goalText(): string {
    const g = this.mode.goal;
    const bits: string[] = [];
    if (g.rating !== undefined) bits.push(`rating ${g.rating}`);
    if (g.pop !== undefined) bits.push(`${g.pop} people`);
    if (g.meanSat !== undefined) bits.push(`mood ${Math.round(g.meanSat * 100)}%`);
    if (g.popFraction !== undefined) bits.push(`keep ${Math.round(g.popFraction * 100)}% of your tenants`);
    return bits.join(' · ');
  }
}

function blankAgent(id: number): Agent {
  return {
    id,
    live: false,
    unit: -1,
    storey: 0,
    legTo: 0,
    finalTo: 0,
    state: 'walk',
    riser: -1,
    dir: 1,
    car: -1,
    col: 0,
    fromCol: 0,
    targetCol: 0,
    walkFrom: 0,
    wake: 0,
    waitStart: 0,
    waitTicks: 0,
    rideTicks: 0,
    walkTicks: 0,
    rideStart: 0,
  };
}

const clamp = (v: number, lo: number, hi: number): number => (v < lo ? lo : v > hi ? hi : v);
const sum = (a: Float64Array): number => {
  let s = 0;
  for (const v of a) s += v;
  return s;
};
const sumInt = (a: Int32Array): number => {
  let s = 0;
  for (const v of a) s += v;
  return s;
};
